import { apiGatewayClient } from '@/common/apis/client';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { useQuery } from '@tanstack/react-query';

export interface PaymentMethod {
  payment_method: string;
  title: string;
  display_amount: string;
  logo: string;
  html_content: string;
  additional_html?: string;
}

interface Params {
  amount?: string;
  timezone?: string;
  countryCode?: string;
  center_id?: string;
  /** فقط وقتی سرویس balance با موفقیت مقدار داده باشد ارسال می‌شود */
  balance?: number;
}

type Response = {
  data: {
    payment_methods: PaymentMethod[];
    additional_html?: string;
    payment_description_html?: string;
    scroll_to_payment_methods?: boolean;
  };
};

export const getPaymentMethods = async (params: Params) => {
  const query: Record<string, string | undefined> = {
    amount: params.amount,
    timezone: params.timezone,
    'country-code': params.countryCode,
    center_id: params.center_id,
  };
  if (typeof params.balance === 'number' && !Number.isNaN(params.balance)) {
    query.balance = String(params.balance);
  }
  return apiGatewayClient.get<Response>(`/katibe/v1/payment-methods/p24`, {
    params: query,
  });
};

// The katibe payment-methods call takes 1–2s; the list rarely changes between visits, so the last
// response this browser saw (per center) is shown at once and replaced when the fresh one arrives.
const lastMethodsKey = (centerId?: string) => `p24:last-payment-methods:${centerId ?? ''}`;
const readLastMethods = (centerId?: string) => {
  try {
    const raw = window.localStorage.getItem(lastMethodsKey(centerId));
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};
const saveLastMethods = (centerId: string | undefined, data: unknown) => {
  try {
    window.localStorage.setItem(lastMethodsKey(centerId), JSON.stringify({ data: (data as any)?.data }));
  } catch {
    // storage unavailable — fine, the fresh list still shows
  }
};

export const useGetPaymentMethods = (params: Params, options?: any) => {
  return useQuery([ServerStateKeysEnum.PaymentMethods, params], () => getPaymentMethods(params), {
    ...options,
    placeholderData: (previous: unknown) => previous ?? readLastMethods(params.center_id),
    onSuccess: (data: unknown) => {
      saveLastMethods(params.center_id, data);
      options?.onSuccess?.(data);
    },
  });
};

